import { Languages } from "../enums";
import { MenuItem } from "../interfaces";
import { EngagementFlowWithProgramEngagementFlowDto, Program, SetProgramLanguagesDto, SubscriptionInfo } from "../services/api";

// Custom earning flow slot ordering. Both the list page and the editor
// page derive their layout from this list, so keep the source of truth here.
export const CUSTOM_FLOW_SLOTS: EngagementFlowWithProgramEngagementFlowDto.NameEnum[] = [
  EngagementFlowWithProgramEngagementFlowDto.NameEnum.Custom1,
  EngagementFlowWithProgramEngagementFlowDto.NameEnum.Custom2,
  EngagementFlowWithProgramEngagementFlowDto.NameEnum.Custom3,
  EngagementFlowWithProgramEngagementFlowDto.NameEnum.Custom4,
  EngagementFlowWithProgramEngagementFlowDto.NameEnum.Custom5,
];

export const DEFAULT_LANGUAGE = Languages.en;

export const AVAILABLE_LANGUAGES = [Languages.en, Languages.es, Languages.de, Languages.it, Languages.fr];

export const PAGINATION_SIZE = 10;


export const SHOPIFY_HOST = ".myshopify.com";

// After an outage, automatic health checks reload the app at most twice per minute, so an API that
// answers /health but still fails the loads cannot trap the merchant in a reload loop.
export const SERVER_RECOVERY_RELOAD_KEY = "serverRecoveryReloadAt";
export const SERVER_RECOVERY_RELOAD_COOLDOWN_MS = 60_000;
export const SERVER_RECOVERY_RELOADS_PER_COOLDOWN = 2;
// A health check that does not answer in time counts as failed, so a hanging API still shows the outage notice.
export const SERVER_HEALTH_CHECK_TIMEOUT_MS = 10_000;

// The notice asking a program still on LoyaltyLion's token and secret to switch to an API key opens once per session
// (sessionStorage, one key per program)
export const LOYALTY_LION_API_KEY_NOTICE_KEY = "loyaltyLionApiKeyNoticeShown";

// Older tiers use this asset URL to represent an image-less state.
export const LEGACY_DEFAULT_TIER_IMAGE = "https://brand-assets.jericommerce.com/tiers/default.png";

// The API's wallet overlay default: a transparent image that stands for "no initial banner".
export const DEFAULT_WALLET_OVERLAY = "https://brand-assets.jericommerce.com/defaults/transparent.png";

export const SHOPIFY_MULTI_PASS_DOCS = "https://shopify.dev/docs/api/multipass";

export const CONVERCUS_DOCS_URL = "https://www.convercus.com/";

export const SUPPORT_DOCS_URL = "https://support.jericommerce.com";
export const CHANGELOG_URL = "https://support.jericommerce.com/changelog";

export const INTERCOM_APP_ID = "dcxyq5wj";
export const CHANGELOG_RSS_URL = "https://support.jericommerce.com/changelog/rss.xml";
export const CHANGELOG_FRESHNESS_DAYS = 7;
// Daily EUR exchange rates from the same provider the API uses; the fallback is the provider's documented mirror.
export const EUR_EXCHANGE_RATES_URL = "https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/eur.min.json";
export const EUR_EXCHANGE_RATES_FALLBACK_URL = "https://latest.currency-api.pages.dev/v1/currencies/eur.min.json";
export const API_DOCS_URL = "https://support.jericommerce.com/integrations/api/1-authentication-through-the-api";

export const CAMPAIGN_COUPONS_DOCS = "https://support.jericommerce.com/loyalty/coupons/how-to-use-coupons";
export const ANONYMOUS_COUPONS_DOCS = "https://support.jericommerce.com/loyalty/coupons/how-to-use-coupons";
export const EMAILS_CUSTOMIZATION_DOCS = "https://support.jericommerce.com/customers/emails-customization";
export const LOYALTY_LION_DOCS = "https://support.jericommerce.com/integrations/loyaltylion/configuration-loyaltylion-integration";
export const LOYALTY_LION_API_KEYS_HELP = "https://help.loyaltylion.com/en/articles/11581435-api-keys";
export const MULTI_PASS_DOCS = "https://support.jericommerce.com/integrations/shopify/shopify-multipass-customers-login-automatically";
export const ENABLE_PUSHES_DOCS = "https://support.jericommerce.com/wallet/im-not-receiving-notifications-on-my-wallet-what-can-i-do";
export const PUBLIC_CAMPAIGNS_DOCS = "https://support.jericommerce.com/loyalty/coupons/how-to-use-coupons";
export const EARNING_FLOW_ACTIONS_DOCS = {
  buttonClick: "https://support.jericommerce.com/loyalty/earning-flows/custom-earning-flows/#tap-to-earn",
  buttonExternal: "https://support.jericommerce.com/loyalty/earning-flows/custom-earning-flows/#external-trigger-and-button",
  external: "https://support.jericommerce.com/loyalty/earning-flows/custom-earning-flows/#external-trigger-only",
};

export const URL_PATTERN = /^(https?:\/\/)?([\da-z.\-]+)\.([a-z.]{2,6})[\/\w .\-]*\/?(\?[\w&=%+.\-]*)?(#[\w\-]*)?$/;

export const VISIBLE_VARIABLES: MenuItem[] = [
  { title: "externalCustomerId", value: "externalCustomerId" },
  { title: "email", value: "email" },
  { title: "name", value: "name" },
  { title: "firstName", value: "firstName" },
  { title: "lastName", value: "lastName" },
  { title: "phone", value: "phone" },
  { title: "loyaltyCustomerId", value: "loyaltyCustomerId" },
  { title: "balance", value: "balance" },
  { title: "balanceName", value: "balanceName" },
  { title: "memberSince", value: "memberSince" },
  { title: "tierStatus", value: "tierStatus" },
  { title: "tierPoints", value: "tierPoints" },
];

export const MULTI_PASS = "multiPass";
export const MULTI_PASS_DEFAULT = "default";
export const WEBAPP_URL = "webappUrl";
export const REFERRAL = "referral";

// Integrations we don't support yet; their cards open the help chat so the merchant can ask for them
export const REQUESTABLE_INTEGRATIONS = ["judge-me", "recharge", "reviews-io"];

export const LINK_VARIABLES: MenuItem[] = [
  {
    title: WEBAPP_URL,
    children: [
      { title: "linkCustomerAppRewards", value: "/loyalty/rewards" },
      { title: "linkCustomerAppEarnings", value: "/loyalty/earnings" },
      { title: "linkCustomerAppCoupons", value: "/coupons" },
      { title: "linkCustomerAppTiers", value: "/loyalty/tiers" },
      { title: "linkCustomerAppOrders", value: "/orders" },
      { title: "linkCustomerAppReferrals", value: "/referrals" },
      { title: "linkCustomerAppProfile", value: "/profile" },
    ],
    value: null,
  },
  {
    title: MULTI_PASS,
    integration: Program.IntegrationEnum.Shopify,
    value: MULTI_PASS,
  },
];

export const COUPON_VARIABLE: MenuItem = { title: "coupon", value: "coupon" };

export const BASE_FEATURES = ["events", "loyalty", "segments", "users"];

// Claude (MCP): an opt-in integration with no features, listed while its catalog row is active
export const MCP_INTEGRATION_NAME = "mcp";

// The OAuth scope a connection holds when it can act on the program, not only read it
export const MCP_WRITE_SCOPE = "loyalty:write";

export const SUBSCRIPTION_PRICES_AND_LIMITS: Record<SubscriptionInfo.TypeEnum, {
  price: number;
  freePasses: number;
  passesUsageChargeAmount: number;
  passesPerUsageCharge: number;
  freeTransactions: number;
  transactionsUsageChargeAmount: number;
  transactionsPerUsageCharge: number;
}> = {
  "free": {
    price: 0.00,
    freePasses: 50,
    passesUsageChargeAmount: NaN,
    passesPerUsageCharge: 1_000,
    freeTransactions: 200,
    transactionsUsageChargeAmount: NaN,
    transactionsPerUsageCharge: 50,
  },
  "growth": {
    price: 45.00,
    freePasses: 1_000,
    passesUsageChargeAmount: 25.00,
    passesPerUsageCharge: 1_000,
    freeTransactions: 750,
    transactionsUsageChargeAmount: 20.00,
    transactionsPerUsageCharge: 100,
  },
  "advanced": {
    price: 145.00,
    freePasses: 1_500,
    passesUsageChargeAmount: 25.00,
    passesPerUsageCharge: 1_000,
    freeTransactions: 4_000,
    transactionsUsageChargeAmount: 8.00,
    transactionsPerUsageCharge: 100,
  },
  "enterprise": {
    price: 100.00,
    freePasses: 0,
    passesUsageChargeAmount: NaN,
    transactionsUsageChargeAmount: NaN,
    freeTransactions: 0,
    passesPerUsageCharge: NaN,
    transactionsPerUsageCharge: NaN,
  },
};

export interface LocaleInfo {
  code: SetProgramLanguagesDto.LocalesEnum;
  nativeName: string;
  translationKey: string;
}

export const AVAILABLE_LOCALES: LocaleInfo[] = [
  { code: SetProgramLanguagesDto.LocalesEnum.En, nativeName: "English", translationKey: "LANGUAGES.en" },
  { code: SetProgramLanguagesDto.LocalesEnum.Es, nativeName: "Español", translationKey: "LANGUAGES.es" },
  { code: SetProgramLanguagesDto.LocalesEnum.De, nativeName: "Deutsch", translationKey: "LANGUAGES.de" },
  { code: SetProgramLanguagesDto.LocalesEnum.Fr, nativeName: "Français", translationKey: "LANGUAGES.fr" },
  { code: SetProgramLanguagesDto.LocalesEnum.It, nativeName: "Italiano", translationKey: "LANGUAGES.it" },
  { code: SetProgramLanguagesDto.LocalesEnum.Bg, nativeName: "Български", translationKey: "LANGUAGES.bg" },
  { code: SetProgramLanguagesDto.LocalesEnum.Ca, nativeName: "Català", translationKey: "LANGUAGES.ca" },
  { code: SetProgramLanguagesDto.LocalesEnum.Hu, nativeName: "Magyar", translationKey: "LANGUAGES.hu" },
];

export const LOYALTY_DASHBOARD_ID = 70;
export const CAMPAIGNS_CARDS_DASHBOARD_ID = 71;
export const DEFAULT_HOLDING_PERIOD_DAYS = 7;
